// hooks/useAnnouncements.js
import { useState, useEffect, useMemo, useCallback, useRef } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { listAnnouncements } from "../graphql/queries";
import {
  onCreateAnnouncement,
  onUpdateAnnouncement,
  onDeleteAnnouncement,
} from "../graphql/subscriptions";

import { refreshApp } from "../utils/appUtils";
import { APP_UPDATE_DELAY, APP_UPDATE_POLL_INTERVAL } from "./constants";

/**
 * useAnnouncements — manages announcement visibility and the force-refresh popup.
 *
 * ─── REQUIREMENTS TABLE ────────────────────────────────────────────────────────
 *
 * | Event              | In visibleAnnouncements           | Refresh popup                    |
 * |--------------------|-----------------------------------|----------------------------------|
 * | Activated          | Yes                               | Yes if forceRefresh              |
 * | Deactivated        | Yes until refresh/dismiss         | Yes until refresh/Cancel         |
 * | Deleted            | No                                | No                               |
 * | User dismiss (X)   | No (normal only)                  | N/A                              |
 * | User Update Now    | No (lastSeenAt / lastAppRefreshAt)| No                               |
 * | User Cancel        | Yes until delay refresh           | No, then refresh after delay     |
 *
 * ─── BEHAVIOR DETAILS ──────────────────────────────────────────────────────────
 *
 * Activated (created active, or edited inactive -> active):
 *   - Announcement shows in appBar.
 *   - Refresh popup opens if forceRefresh.
 *   - Announcements created inactive are NOT added to local state; they enter
 *     local state only when activated via onUpdate (upsert).
 *
 * Deactivated (edited active -> inactive):
 *   - AppBar: stays visible until user refreshes (Update Now / Cancel delay)
 *     or dismisses with X (normal only).
 *   - Popup: stays open for forceRefresh until user refreshes or presses Cancel.
 *   - forceRefresh deactivation: the local copy preserves the prior updatedAt
 *     (the value from when the announcement was last active) so the server-side
 *     deactivation timestamp does not push it past lastSeenAt and revive it.
 *   - Normal deactivation: uses lastAppRefreshAt — cleared by app refresh or X.
 *
 * Deleted: removed from local state immediately; hidden from appBar and popup.
 *
 * ─── DISMISSAL MECHANISMS ──────────────────────────────────────────────────────
 *
 *   1. Per-announcement X dismiss (normal only) — sets dismissedAtMap[id].
 *      Does not apply to forceRefresh (UI shows refresh icon, not X).
 *   2. App refresh via handleUpdate() — sets:
 *      - lastSeenAt (= max forceRefresh updatedAt + 1) — clears forceRefresh.
 *      - lastAppRefreshAt (= Date.now()) — clears deactivated normal.
 *      Then calls refreshApp() to reload the page.
 *
 * ─── PERSISTED STATE (localStorage) ────────────────────────────────────────────
 *
 *   allAnnouncements   — array of announcement objects in local state.
 *   dismissedAt         — map of { [announcementId]: timestamp } for X-dismissed.
 *   lastSeenAt          — timestamp; forceRefresh with updatedAt <= this are hidden.
 *   confirmDismissedAt  — timestamp; popup hidden for forceRefresh with updatedAt <= this.
 *   lastAppRefreshAt    — timestamp; deactivated normal with updatedAt <= this are hidden.
 */
export function useAnnouncements() {
  // 1. Persisted list of all announcements
  const [all, setAll] = useState(() => {
    const saved = localStorage.getItem("allAnnouncements");
    return saved ? JSON.parse(saved) : [];
  });

  // 2. Persisted dismiss-timestamps per announcement ID (X-dismiss for normal only)
  const [dismissedAtMap, setDismissedAtMap] = useState(() => {
    const saved = localStorage.getItem("dismissedAt");
    return saved ? JSON.parse(saved) : {};
  });

  // 3. Persisted forceRefresh gate: set to max(forceRefresh updatedAt) + 1 on refresh.
  // forceRefresh announcements with updatedAt <= lastSeenAt are hidden.
  const [lastSeenAt, setLastSeenAt] = useState(() => {
    return Number(localStorage.getItem("lastSeenAt")) || 0;
  });

  // 4. Persisted popup gate: set to Date.now() when user presses Cancel.
  // Popup hidden for forceRefresh with updatedAt <= confirmDismissedAt.
  const [confirmDismissedAt, setConfirmDismissedAt] = useState(() => {
    return Number(localStorage.getItem("confirmDismissedAt")) || 0;
  });

  // 5. Persisted refresh gate for deactivated normal announcements.
  // Set to Date.now() in handleUpdate(). Deactivated normal with
  // updatedAt <= lastAppRefreshAt are hidden (cleared by refresh).
  const [lastAppRefreshAt, setLastAppRefreshAt] = useState(() => {
    return Number(localStorage.getItem("lastAppRefreshAt")) || 0;
  });

  // Per-tab fetch throttle (do not persist across reloads, so deletes clear quickly)
  const lastFetchedAtRef = useRef(0);

  // Utility: persist a key
  const persist = (key, val) => localStorage.setItem(key, JSON.stringify(val));

  // Merge‐and‐persist helper
  const updateAll = useCallback((updater) => {
    setAll((curr) => {
      const next = updater(curr);
      localStorage.setItem("allAnnouncements", JSON.stringify(next));
      return next;
    });
  }, []);

  // 6. Fetch / merge announcements from server.
  //    - Active announcements: always added/updated.
  //    - Inactive announcements: updated if already in local state, never added fresh.
  //    - Inactive forceRefresh: local updatedAt is preserved (not overwritten by server)
  //      so deactivation timestamp doesn't revive past lastSeenAt.
  //    - Deleted announcements: pruned from local state and dismissedAtMap.
  const fetchAnnouncements = useCallback(async () => {
    const lastFetchedAt = Number(lastFetchedAtRef.current || 0);
    if (Date.now() - lastFetchedAt < APP_UPDATE_POLL_INTERVAL) return;

    // proceed to fetch after POLL_INTERVAL (per tab)
    lastFetchedAtRef.current = Date.now();
    const tenantId = window.appUser?.reloadYardId;

    // Single listAnnouncements call is the source of truth for all
    // announcements that still exist on the server (for this tenant
    // or global). Because AppSync applies the filter after DynamoDB,
    // individual pages may contain few or no matching items, so we
    // iterate carefully over pages, guarding against null/undefined.
    const serverItems = [];
    let nextToken;

    do {
      // eslint-disable-next-line no-await-in-loop
      const resp = await API.graphql(
        graphqlOperation(listAnnouncements, {
          filter: tenantId
            ? {
                or: [
                  { tenantId: { eq: tenantId } },
                  { tenantId: { attributeExists: false } },
                ],
              }
            : undefined,
          limit: 200,
          nextToken,
        }),
      );

      const page = resp?.data?.listAnnouncements;
      if (!page) break;

      const items = Array.isArray(page.items)
        ? page.items.filter((a) => a && a.id)
        : [];

      // Extra safety: enforce tenant / global scope on the client too
      items.forEach((a) => {
        if (!a.tenantId || a.tenantId === tenantId) {
          serverItems.push(a);
        }
      });

      nextToken = page.nextToken;
    } while (nextToken);

    const serverIdSet = new Set(serverItems.map((a) => a.id));

    // Merge announcements from polling with anything already in local
    // state, then:
    //  - keep entries whose IDs still exist on the server
    //  - keep entries that match the current tenant/global
    //  - drop deleted or other-tenant announcements automatically
    //  - keep existing inactive announcements until dismissed/deleted,
    //    but do NOT add brand-new inactive announcements that were
    //    never present locally.
    let nextAll;
    setAll((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      serverItems.forEach((a) => {
        if (!a || !a.id) return;
        const alreadyExists = map.has(a.id);
        if (a.active === "true" || alreadyExists) {
          // For inactive forceRefresh that already exists locally: preserve the
          // local updatedAt so the server-side deactivation timestamp doesn't
          // revive it past lastSeenAt after the user has already refreshed.
          if (alreadyExists && a.active !== "true" && a.forceRefresh) {
            map.set(a.id, { ...a, updatedAt: map.get(a.id).updatedAt });
          } else {
            map.set(a.id, a);
          }
        }
      });

      nextAll = Array.from(map.values()).filter((a) => {
        if (!a || !a.id) return false;
        if (!serverIdSet.has(a.id)) return false;
        if (a.tenantId && a.tenantId !== tenantId) return false;
        return true;
      });

      localStorage.setItem("allAnnouncements", JSON.stringify(nextAll));
      return nextAll;
    });

    // Prune dismissedAt for announcements that no longer exist or are out-of-tenant
    if (nextAll) {
      const allowedIds = new Set(nextAll.map((a) => a.id));
      setDismissedAtMap((curr) => {
        const pruned = Object.fromEntries(
          Object.entries(curr || {}).filter(([id]) => allowedIds.has(id)),
        );
        persist("dismissedAt", pruned);
        return pruned;
      });
    }
  }, []);

  // 7. Polling
  useEffect(() => {
    // Always do an initial sync on mount so deleted announcements
    // don't linger from localStorage across reloads.
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // 8. Subscriptions (real-time updates from AppSync).
  //    - onCreate: only adds active announcements (inactive created on server are ignored).
  //    - onUpdate: upserts active (including newly activated); for inactive forceRefresh,
  //      preserves local updatedAt to prevent deactivation from reviving past lastSeenAt.
  //    - onDelete: removes from local state and dismissedAtMap.
  useEffect(() => {
    const { handleUnauthorizedError } = require("../utils/appUtils");
    const tenantId = window.appUser?.reloadYardId;

    const subC = API.graphql(graphqlOperation(onCreateAnnouncement)).subscribe({
      next: ({ value }) => {
        const a = value?.data?.onCreateAnnouncement;
        // Only append if active and for our tenant; inactive are not added.
        if (a?.active === "true" && (!a.tenantId || a.tenantId === tenantId)) {
          // Upsert by id (not blind append) to avoid duplicates when AppSync
          // replays events (e.g. reconnect) and to keep the latest payload.
          updateAll((curr) => {
            const idx = curr.findIndex((x) => x.id === a.id);
            if (idx === -1) return [...curr, a];
            return curr.map((x) => (x.id === a.id ? a : x));
          });
        }
      },
      error: (error) => {
        handleUnauthorizedError(error);
      },
    });
    const subU = API.graphql(graphqlOperation(onUpdateAnnouncement)).subscribe({
      next: ({ value }) => {
        const a = value?.data?.onUpdateAnnouncement;
        if (!a?.id) return;
        updateAll((curr) => {
          // If this announcement is now for a different tenant, remove it
          if (a.tenantId && a.tenantId !== tenantId) {
            return curr.filter((x) => x.id !== a.id);
          }

          // For matching tenant/global:
          // - if active === "true", upsert the latest version
          // - if inactive, update existing entry if present (do not create new)
          if (a.active === "true") {
            // Upsert: if the announcement wasn't in local state (e.g. created
            // inactive and now activated), append it; otherwise update in place.
            const idx = curr.findIndex((x) => x.id === a.id);
            if (idx === -1) return [...curr, a];
            return curr.map((x) => (x.id === a.id ? a : x));
          }
          const exists = curr.some((x) => x.id === a.id);
          if (exists) {
            return curr.map((x) => {
              if (x.id !== a.id) return x;
              // For forceRefresh going inactive: preserve the prior updatedAt so
              // the server-side deactivation timestamp doesn't revive it past
              // lastSeenAt after the user has already refreshed.
              if (a.forceRefresh) {
                return { ...a, updatedAt: x.updatedAt };
              }
              return a;
            });
          }
          return curr;
        });
      },
      error: (error) => {
        handleUnauthorizedError(error);
      },
    });
    const subD = API.graphql(graphqlOperation(onDeleteAnnouncement)).subscribe({
      next: ({ value }) => {
        const id = value?.data?.onDeleteAnnouncement?.id;
        if (!id) return;
        // Filter out any old copy of this announcement
        updateAll((curr) => curr.filter((x) => x.id !== id));

        // Also remove any dismissal timestamp for this announcement
        setDismissedAtMap((curr) => {
          if (!curr || !curr[id]) return curr;
          const { [id]: _, ...rest } = curr;
          persist("dismissedAt", rest);
          return rest;
        });
      },
      error: (error) => {
        handleUnauthorizedError(error);
      },
    });
    return () => {
      subC.unsubscribe();
      subU.unsubscribe();
      subD.unsubscribe();
    };
  }, [updateAll]);

  // 9. Dismiss a normal announcement
  const dismiss = useCallback(
    (id) => {
      const now = Date.now();
      const next = { ...dismissedAtMap, [id]: now };
      setDismissedAtMap(next);
      persist("dismissedAt", next);
    },
    [dismissedAtMap],
  );

  // 10. Handle "Update Now" or delayed refresh after Cancel.
  // Sets lastSeenAt (clears forceRefresh) and lastAppRefreshAt (clears deactivated normal),
  // then reloads the app via refreshApp().
  const handleUpdate = useCallback(() => {
    const maxForce = Math.max(
      0,
      ...all
        .filter((a) => a.forceRefresh)
        .map((a) => new Date(a.updatedAt).getTime()),
    );
    const seen = maxForce + 1;
    setLastSeenAt(seen);
    persist("lastSeenAt", seen);

    // Record the refresh time so deactivated normal announcements are also
    // cleared on refresh (they use lastAppRefreshAt, not lastSeenAt).
    const refreshedAt = Date.now();
    setLastAppRefreshAt(refreshedAt);
    persist("lastAppRefreshAt", refreshedAt);

    // persist current list too (so future mounts have it)
    localStorage.setItem("allAnnouncements", JSON.stringify(all));

    refreshApp();
  }, [all]);

  // 11. Handle "Cancel" on Confirm dialog.
  // Closes popup (confirmDismissedAt), then schedules handleUpdate after APP_UPDATE_DELAY.
  const confirmClose = useCallback(() => {
    const now = Date.now();
    setConfirmDismissedAt(now);
    persist("confirmDismissedAt", now);

    setTimeout(() => handleUpdate(), APP_UPDATE_DELAY);
  }, [handleUpdate]);

  // 12. Compute visible announcements (tenant-scoped).
  // forceRefresh (active or inactive): visible until refresh (lastSeenAt); no X dismiss.
  // Normal active: visible if updated since X dismiss (dismissedAt).
  // Normal inactive: visible until X dismiss OR app refresh (lastAppRefreshAt).
  const visibleAnnouncements = all.filter((a) => {
    if (!a || !a.id) return false;

    const tenantId = window.appUser?.reloadYardId;

    if (a.tenantId && a.tenantId !== tenantId) {
      return false;
    }
    const dismissedAt = dismissedAtMap[a.id] || 0;
    const updatedAt = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;

    if (a.forceRefresh) {
      // Only refresh counts as dismissal; show until then (active or inactive).
      return updatedAt > lastSeenAt;
    }
    if (a.active !== "true") {
      // Normal inactive: visible until user dismisses (X) OR app refresh.
      // Hide if dismissed, or if an app refresh happened after updatedAt.
      if (dismissedAt > 0) return false;
      return updatedAt > lastAppRefreshAt;
    }
    // Normal active: show if updated since last dismiss.
    return updatedAt > dismissedAt;
  });

  // 13. Should Confirm dialog be open?
  // Show for any visible forceRefresh (active OR deactivated) until refresh or Cancel.
  // Cancel sets confirmDismissedAt which hides popup; delayed refresh then clears everything.
  const showConfirm = useMemo(() => {
    return visibleAnnouncements.some((a) => {
      if (!a.forceRefresh) return false;
      const updatedAt = new Date(a.updatedAt).getTime();
      return updatedAt > confirmDismissedAt;
    });
  }, [visibleAnnouncements, confirmDismissedAt]);

  return {
    visibleAnnouncements,
    showConfirm,
    dismiss,
    handleUpdate,
    confirmClose,
  };
}
